import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { startLoading, stopLoading } from '../../../features/loader/loaderSlice';
import { closeSnackbar, openSnackbar } from '../../../features/snackbar/snackbarslice';
import { SNACKBAR_TYPES } from '../../../constants/constant';
import { URLS } from '../../../constants';

// Base fetchQuery setup
const baseFetchQuery = fetchBaseQuery({
  credentials: 'include',
  prepareHeaders: (headers, { endpoint }) => {
    if (endpoint !== 'uploadFileServiceApi') {
      headers.set('Content-Type', 'application/json');
    } else {
      headers.delete('Content-Type');
    }
    return headers;
  },
  responseHandler: (response) => {
    const ct = response.headers.get('Content-Type') || '';
    if (ct.includes('text/html')) return response.text();
    return response.json();
  },
});

let activeRequests = 0;
let refreshTokenPromise = null;

// Helper to refresh the token
const fetchRefreshToken = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_V2_URL}/refresh-token`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to refresh token');
    return true;
  } catch (err) {
    console.error('Refresh token failed:', err);
    window.location.href = `${process.env.REACT_APP_SSO_BACKEND_BUILD_URL}`;
    sessionStorage.clear();
    return false;
  }
};

// The custom baseQuery
export const baseQuery = async (args, api, extraOptions) => {
  const { dispatch } = api;
  const isExcluded = ['FetchChatGpt', 'acknowlegeChatGpt'].includes(api.endpoint);

  // Determine which base URL to use
  let baseUrl = process.env.REACT_APP_CLARITY_BACKEND_BUILD_URL;
  if (args.url === URLS.USER_LIST || args.url === URLS.USER_DETAILS) {
    baseUrl = process.env.REACT_APP_SSO_BACKEND_BUILD_URL;
  } else if (args.url === URLS.LOGOUT_URL || args.url === URLS.REFRESH_TOKEN) {
    baseUrl = process.env.REACT_APP_BASE_V2_URL;
  }

  // Normalize args.url to fully qualified URL
  if (typeof args === 'string') {
    args = `${baseUrl}${args}`;
  } else if (args.url && !args.url.startsWith('http')) {
    args.url = `${baseUrl}${args.url}`;
  }

  // Start loader if needed
  if (!isExcluded) {
    if (activeRequests === 0) dispatch(startLoading());
    activeRequests++;
  }

  try {
    // Perform the request
    let result = await baseFetchQuery(args, api, extraOptions);

    // Handle 401 -> refresh token
    if (result.error?.status === 401) {
      if (!refreshTokenPromise) {
        refreshTokenPromise = fetchRefreshToken().finally(() => {
          refreshTokenPromise = null;
        });
      }
      const ok = await refreshTokenPromise;
      if (ok) {
        result = await baseFetchQuery(args, api, extraOptions);
      } else {
        // If refresh ultimately failed
        window.location.href = process.env.REACT_APP_LOGIN_URL;
        sessionStorage.clear();
        return { error: { status: 401, data: { message: 'Session expired' } } };
      }
    }

    // Handle any other error
    if (result.error) {
      // Specific 403 redirect
      if (api.endpoint === 'getProjectDataFromProjectId' && result.error.status === 403) {
        window.location.href = '/';
      }

      // Snackbar
      dispatch(openSnackbar({
        message: result.error.data?.errorMessage || 'An error occurred',
        type: SNACKBAR_TYPES.ERROR,
      }));
      setTimeout(() => dispatch(closeSnackbar()), 10000);

      return { error: { status: result.error.status, data: result.error.data } };
    }

    // Success
    return result;

  } finally {
    // Always decrement and possibly stop loader
    if (!isExcluded) {
      activeRequests--;
      if (activeRequests === 0) {
        dispatch(stopLoading());
      }
    }
  }
};

// Custom base query with error handling
// export const baseQuery = async (args, api, extraOptions) => {
//     const { dispatch } = api
   
//     // Exclude both 'FetchChatGpt' and 'ACKNOWLEDGE_CHAT_GPT' from triggering the loader
//     const isExcluded = ['FetchChatGpt', 'acknowlegeChatGpt'].includes(api?.endpoint);

//     if (!isExcluded && activeRequests === 0) {
//         dispatch(startLoading());
//     }
//     activeRequests += 1;
//     try {
//         const data = await baseFetchQuery(args, api, extraOptions);
//         // Check if there's an error in the result
//         if (data?.error) {
//             throw data.error;
//         }
//         activeRequests -= 1;
//         if (!isExcluded &&activeRequests === 0) {
//             dispatch(stopLoading());
//         }
//         return data;
//     } catch (error) {
//         activeRequests -= 1;
//         if (!isExcluded &&activeRequests === 0) {
//             dispatch(stopLoading());
//         }
//         if(error?.status === 401) {
//             window.location.href = process.env.REACT_APP_LOGIN_URL;
//         }
//         // Check if the error comes from 'getProjectDataFromProjectId' and status is 403
//         if (api?.endpoint === 'getProjectDataFromProjectId' && error?.status === 403) {
//             window.location.href = '/';
//         }
        
//         dispatch(openSnackbar({ message: error?.data?.errorMessage,type: SNACKBAR_TYPES.ERROR,}));
//         setTimeout(() => {
//             dispatch(closeSnackbar());
//           }, 10000);
//         return { error: { status: error?.status, data: error?.data } };
//     }
// };

